// Shared structured-address helpers, used by <AddressFields> (single address)
// and <AddressListInput> (multi-row person addresses). The street is entered as
// house number + street name + street type but stored as a single `line1`
// ("123 Main St"); these compose/parse that mapping, and convert a row to/from
// the structured DB columns shape.
import { normalizeStateCode } from '@stfors/shared-types';

// Common USPS street-type suffixes for the street-type dropdown.
export const STREET_TYPES = [
  'St', 'Ave', 'Blvd', 'Rd', 'Ln', 'Dr', 'Ct', 'Pl', 'Way', 'Ter', 'Cir',
  'Pkwy', 'Hwy', 'Sq', 'Trl', 'Loop', 'Run', 'Path', 'Pike', 'Row', 'Walk',
  'Cove', 'Xing', 'Aly', 'Pt',
];

// USPS directionals for the pre-/post-direction dropdowns. Client-side fallback
// mirroring the street_direction lookup seed (migration 0040).
export const STREET_DIRECTIONS = ['N', 'S', 'E', 'W', 'NE', 'NW', 'SE', 'SW'];

export interface AddressValue {
  houseNumber: string;
  streetDirection: string;      // pre-directional, e.g. "E" in "123 E Jefferson St"
  streetName: string;
  streetType: string;
  streetPostDirection: string;  // post-directional, e.g. "NW" in "1300 Pennsylvania Ave NW"
  line2: string;
  city: string;
  state: string;        // 2-letter code
  postal_code: string;
  country: string;
}

export const emptyAddressValue = (): AddressValue => ({
  houseNumber: '', streetDirection: '', streetName: '', streetType: '',
  streetPostDirection: '', line2: '',
  city: '', state: '', postal_code: '', country: '',
});

type StreetParts = Pick<
  AddressValue,
  'houseNumber' | 'streetDirection' | 'streetName' | 'streetType' | 'streetPostDirection'
>;

export function composeStreet(a: StreetParts): string {
  return [a.houseNumber, a.streetDirection, a.streetName, a.streetType, a.streetPostDirection]
    .map((s) => s.trim()).filter(Boolean).join(' ');
}

// Match a token against the known directionals (period-tolerant), returning the
// canonical upper-case form ("e." → "E") or '' if it isn't a direction.
function matchDirection(token: string): string {
  const norm = token.replace(/\.+$/, '').toLowerCase();
  return STREET_DIRECTIONS.find((d) => d.toLowerCase() === norm) ?? '';
}

// Best-effort split of a stored "123 E Jefferson St NW" line1 back into parts:
// a leading numeric token is the house number; a trailing directional is the
// post-direction; a trailing token matching a known street type is the street
// type; a leading directional is the pre-direction; the remainder is the name.
// Each directional is only peeled off when at least one token would remain for
// the street name, so a lone directional that IS the name (DC's "E St",
// "1300 N St NW") is preserved.
export function parseStreet(line1: string | null | undefined): StreetParts {
  const tokens = (line1 ?? '').trim().split(/\s+/).filter(Boolean);
  const empty: StreetParts = {
    houseNumber: '', streetDirection: '', streetName: '', streetType: '', streetPostDirection: '',
  };
  if (tokens.length === 0) return empty;

  let houseNumber = '';
  // Multi-token: a leading token that starts with a digit is the house number
  // ("123 Main", "12A Oak"). Single pure-digit token alone is also a house
  // number ("123") — without this, compose→parse mid-edit would shove the
  // digits into streetName. A lone alphanumeric like "1st" stays the name.
  if (tokens.length > 1 && /^\d/.test(tokens[0]!)) {
    houseNumber = tokens.shift()!;
  } else if (tokens.length === 1 && /^\d+$/.test(tokens[0]!)) {
    houseNumber = tokens.shift()!;
  }

  let streetPostDirection = '';
  if (tokens.length > 1) {
    const dir = matchDirection(tokens[tokens.length - 1]!);
    if (dir) { streetPostDirection = dir; tokens.pop(); }
  }

  let streetType = '';
  if (tokens.length > 1) {
    // The trailing token may be a street type, often abbreviated with a
    // period ("Ave."). Drop the period before matching and store the canonical
    // dropdown form ("Ave"), so "Von Karman Ave." → name "Von Karman" + "Ave".
    const lastNorm = tokens[tokens.length - 1]!.replace(/\.+$/, '').toLowerCase();
    const match = STREET_TYPES.find((t) => t.toLowerCase() === lastNorm);
    if (match) {
      streetType = match;
      tokens.pop();
    }
  }

  let streetDirection = '';
  if (tokens.length > 1) {
    const dir = matchDirection(tokens[0]!);
    if (dir) { streetDirection = dir; tokens.shift(); }
  }

  return { houseNumber, streetDirection, streetName: tokens.join(' '), streetType, streetPostDirection };
}

export function isAddressEmpty(a: AddressValue): boolean {
  return !Object.values(a).some((v) => v.trim().length > 0);
}

/** The structured columns shape shared by the DB / wire / mutation payloads. */
export interface AddressColumns {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
}

/** Accept either street naming: person addresses use `line1`/`line2`, while
 *  client / contractor / vendor / organization rows use `address_line1`/
 *  `address_line2`. The readers below check both so any such row can be passed
 *  directly without dropping the street. */
export type AddressColumnsLike = Partial<AddressColumns> & {
  address_line1?: string | null;
  address_line2?: string | null;
};

export function addressValueToColumns(a: AddressValue): AddressColumns {
  return {
    line1: composeStreet(a) || null,
    line2: a.line2.trim() || null,
    city: a.city.trim() || null,
    state: a.state.trim() || null,
    postal_code: a.postal_code.trim() || null,
    country: a.country.trim() || null,
  };
}

export function addressValueFromColumns(c: AddressColumnsLike | null | undefined): AddressValue {
  const cols = c ?? {};
  return {
    ...parseStreet(cols.line1 ?? cols.address_line1 ?? null),
    line2: cols.line2 ?? cols.address_line2 ?? '',
    city: cols.city ?? '',
    state: normalizeStateCode(cols.state ?? null) ?? '',
    postal_code: cols.postal_code ?? '',
    country: cols.country ?? '',
  };
}

// The US is the home country, so it's omitted from displayed addresses — only
// foreign countries are shown. Accepts the common spellings/abbreviations.
const US_COUNTRY_NAMES = new Set([
  '', 'us', 'usa', 'u.s.', 'u.s.a.', 'united states', 'united states of america',
]);

/** Country to show in a displayed address, or null to omit it. Returns null for
 *  the US (any common spelling) or a blank value; otherwise the trimmed name. */
export function displayCountry(country: string | null | undefined): string | null {
  const c = (country ?? '').trim();
  return US_COUNTRY_NAMES.has(c.toLowerCase()) ? null : c;
}

/** Compose stored structured columns into a one-line display string (or null).
 *  Country is included only when it's a foreign country (US is omitted). */
export function formatAddressColumns(c: AddressColumnsLike | null | undefined): string | null {
  const cols = c ?? {};
  const cityLine = [cols.city, [cols.state, cols.postal_code].filter(Boolean).join(' ').trim()]
    .filter((s) => s && s.trim().length > 0).join(', ');
  const parts = [cols.line1 ?? cols.address_line1, cols.line2 ?? cols.address_line2, cityLine, displayCountry(cols.country)]
    .filter((s) => s && s.trim().length > 0);
  return parts.length > 0 ? parts.join(', ') : null;
}

/** Compose a single display/query line for Geoapify verify + legacy fallback. */
export function addressToQuery(a: AddressValue): string {
  return [
    composeStreet(a),
    a.line2.trim(),
    [a.city.trim(), a.state.trim(), a.postal_code.trim()].filter(Boolean).join(' '),
  ].filter((s) => s.trim().length > 0).join(', ');
}

/** Geoapify structured components (from GET /api/geocode) → an editable value. */
export function addressValueFromComponents(c: {
  house_number?: string | null; street?: string | null; line2?: string | null;
  city?: string | null; state?: string | null; postal_code?: string | null; country?: string | null;
}): AddressValue {
  const street = parseStreet(c.street ?? null);
  return {
    houseNumber: (c.house_number ?? '').trim() || street.houseNumber,
    streetDirection: street.streetDirection,
    streetName: street.streetName || (c.street ?? '').trim(),
    streetType: street.streetType,
    streetPostDirection: street.streetPostDirection,
    line2: (c.line2 ?? '').trim(),
    city: (c.city ?? '').trim(),
    state: normalizeStateCode(c.state ?? null) ?? '',
    postal_code: (c.postal_code ?? '').trim(),
    country: (c.country ?? '').trim(),
  };
}

/** Fill only blank fields on `current` from `source` (never overwrite typed data).
 *  Used after Verify address succeeds and when auto-parse returns while the user
 *  has already started typing. */
export function fillBlankAddressFields(current: AddressValue, source: AddressValue): AddressValue {
  const pick = (a: string, b: string) => (a.trim().length > 0 ? a : b);
  return {
    houseNumber: pick(current.houseNumber, source.houseNumber),
    streetDirection: pick(current.streetDirection, source.streetDirection),
    streetName: pick(current.streetName, source.streetName),
    streetType: pick(current.streetType, source.streetType),
    streetPostDirection: pick(current.streetPostDirection, source.streetPostDirection),
    line2: pick(current.line2, source.line2),
    city: pick(current.city, source.city),
    state: pick(current.state, source.state),
    postal_code: pick(current.postal_code, source.postal_code),
    country: pick(current.country, source.country),
  };
}
